import Box from './Box'
import GrooverText from './GrooverText'
import { SkeletonCustom } from './FigmaText'
import { Texts } from '../../../pure-js/libs/Texts'
import { Calculation, CalculationDiscount } from '../../../pure-js/libs/calculateLogic/CalculateDiscountTypes'
import { EIGHT_PX, FOUR_PX } from '../enums/Spacings'
import { Discount } from './PriceContainerUserPricing'
import { TextKey } from '../../../pure-js/types/Antiloop'
import { Activity, Client, Currency } from '../../../pure-js/types/GrooverTypes'
import styled from '@emotion/styled'
import { getPriceFromCalculation } from '../../../pure-js/libs/calculateLogic/CalculateDiscountHelpers'
import { State } from '../hooks/useAppState'
import { formatPrice } from '../utils/activityUtils'

type Props = {
  currency: Currency
  calculations: Calculation[]
  isLoading: boolean
  state?: State
  client?: Client
}

type GroupedCalculation = {
  activities: Activity[]
  activitiesTotalPrice: number
  finalPrice: number
  discounts: CalculationDiscount[]
}

type GroupedDiscount = Omit<CalculationDiscount, 'id' | 'input' | 'output'>

const getPeriodName = (activity: Activity, client?: Client): string | undefined => {
  if (activity.periodId && client?.periods) {
    const period = client.periods.find((p) => p.periodId === activity.periodId)
    if (period?.periodName) return period.periodName
  }
  return activity.period || undefined
}

const PriceContainerActivity = ({ currency, calculations, isLoading, state, client }: Props) => {
  const groupedCalculations = calculations.reduce<GroupedCalculation>(
    (acc, calc) => {
      acc.activities = [...acc.activities, calc.activity]
      acc.activitiesTotalPrice += getPriceFromCalculation(calc)
      acc.finalPrice += calc.finalPrice
      acc.discounts = [...acc.discounts, ...calc.discounts]
      return acc
    },
    { activities: [], discounts: [], finalPrice: 0, activitiesTotalPrice: 0 }
  )

  // Calculate original price (before discounts) for display
  const originalPrice = calculations.reduce((sum, calc) => {
    const quantity = state?.singleEventQuantity || 1
    return sum + calc.activity.price * quantity
  }, 0)

  const mainActivity: Activity = groupedCalculations.activities[0]

  const hasMatrixOrCombinedDiscount = groupedCalculations.discounts.some(
    (discount) => discount.type === 'multi_course_matrix' || discount.type === 'combined'
  )

  return (
    <Box fullWidth key={mainActivity.id} top bottom spacing={EIGHT_PX}>
      {ActivityHeader(hasMatrixOrCombinedDiscount, isLoading, state)}
      {Object.entries(
        groupedCalculations.discounts
          .filter((discount) => discount.type === 'multi_course_matrix' || discount.type === 'combined')
          .reduce<Record<string, GroupedDiscount>>((acc, discount) => {
            if (acc[discount.name]) {
              acc[discount.name].discount += discount.discount
            } else {
              acc[discount.name] = {
                type: discount.type,
                name: discount.name,
                discount: discount.discount,
                discountCalculationType: discount.discountCalculationType,
                discountValue: discount.discountValue
              }
            }
            return acc
          }, {})
      ).map(([, groupedDiscount]) => {
        if (groupedDiscount.type === 'multi_course_matrix') {
          const periodName = getPeriodName(mainActivity, client)
          return RenderActivityDiscount(Texts.componentsPricingControlBundleDiscountRow, groupedDiscount, periodName)
        } else if (groupedDiscount.type === 'combined') {
          return RenderActivityDiscount(Texts.componentsPricingControlExtentionDiscountRow, groupedDiscount)
        }
      })}

      {/* Only show activity total sum if we have certain kinds of discounts. Other discounts are shown in the user pricing
      component. */}
      {/* {hasMatrixOrCombinedDiscount && (
        <Box alignSelf="flex-end" top spacing={EIGHT_PX}>
          <FigmaText
            textKey={Texts.componentsPricingControlCssPriceRowAmount}
            text={formatPrice(groupedCalculations.finalPrice, currency)}
          />
        </Box>
      )} */}
    </Box>
  )

  function RenderActivityDiscount(textKey: TextKey, discount: GroupedDiscount, periodName?: string) {
    return (
      <Box gap={FOUR_PX} fullWidth key={discount.name}>
        <Box
          key={discount.name}
          fullWidth
          direction="row"
          align="center"
          justify="space-between"
          top
          spacing={EIGHT_PX}
        >
          <GrooverText textKey={textKey}>{periodName ? ` (${periodName})` : undefined}</GrooverText>

          <Box direction="row" align="center" gap={FOUR_PX}>
            {/* Show relative discount value if percentage (eg. _10%_ (100 SEK)) */}
            {discount.discountCalculationType === 'percentage' && (
              <>
                {' '}
                <span>{`${String(discount.discountValue)}%`}</span>
              </>
            )}
            <Discount fullPadding spacing="2px">
              <span>{`-${formatPrice(discount.discount, currency)}`}</span>
            </Discount>
          </Box>
        </Box>
      </Box>
    )
  }

  function ActivityHeader(hasDiscount: boolean, isLoading: boolean, state?: State) {
    const isSingleEvent = mainActivity.eventType === 'SingleEvent'
    const singleEventQuantity = state?.singleEventQuantity || 1

    return (
      <Box fullWidth direction="row" justify="space-between">
        <Box direction="row" align="center" gap={FOUR_PX}>
          {isLoading ? (
            <SkeletonCustom>
              <span>{mainActivity.name}</span>
            </SkeletonCustom>
          ) : (
            <span>{mainActivity.name}</span>
          )}
          {isSingleEvent &&
            (isLoading ? (
              <SkeletonCustom>
                <span>{`(x${singleEventQuantity})`}</span>
              </SkeletonCustom>
            ) : (
              <span>{`(x${singleEventQuantity})`}</span>
            ))}
        </Box>
        <ActivityHeaderBox>
          {isLoading ? (
            <SkeletonCustom>
              <span>{formatPrice(originalPrice, currency)}</span>
            </SkeletonCustom>
          ) : (
            <span>{formatPrice(originalPrice, currency)}</span>
          )}
        </ActivityHeaderBox>
      </Box>
    )
  }
}

const ActivityHeaderBox = styled(Box)`
  direction: row;
  min-width: fit-content;
  gap: 4px;
  white-space: nowrap;
`

export default PriceContainerActivity
